import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRightLeft,
  Layers,
  ScanSearch,
  ArrowRight,
  ClipboardList,
  Database,
  Settings2,
  GraduationCap,
  Building2,
  Users,
} from "lucide-react";

const RACKS_IMG =
  "https://images.unsplash.com/photo-1592085198739-ffcad7f36b54?crop=entropy&cs=srgb&fm=jpg&q=85";

const SERVICES = [
  {
    id: "mrp-migration",
    icon: ArrowRightLeft,
    num: "01",
    title: "MRP & System Migration",
    tag: "Spreadsheets → automated planning tools",
    intro:
      "Transitioning teams from basic spreadsheets to automated planning tools — without stopping production to do it.",
    includes: [
      "Current-state assessment of your spreadsheets, stock records and reorder habits",
      "Data cleanup: item master, stock levels and consumption history made system-ready",
      "ERP/MRP deployment and database configuration — including Misys and QuickBooks integration",
      "Replenishment logic built on real consumption: reorder points and safety stock thresholds",
      "Hands-on team training so the system is actually used after go-live",
    ],
  },
  {
    id: "bom-optimization",
    icon: Layers,
    num: "02",
    title: "Workflow & BOM Optimization",
    tag: "Multi-level Bills of Materials, precise",
    intro:
      "Structuring multi-level Bills of Materials and tightening shop-floor inventory tracking so raw material availability always matches manufacturing output.",
    includes: [
      "Multi-level BOM structuring and revision control",
      "Master data alignment between planning, purchasing and the floor",
      "Shop-floor tracking for receiving, kitting and staging",
      "Stock-level monitoring across active production cycles",
      "Consumption and usage-rate analysis feeding accurate demand forecasts",
    ],
  },
  {
    id: "auditing-control",
    icon: ScanSearch,
    num: "03",
    title: "Operational Auditing & Control",
    tag: "Shrinkage down, discipline up",
    intro:
      "Reducing shrinkage, optimizing safety stock levels, and establishing repeatable receiving and fulfillment processes that hold up to audit.",
    includes: [
      "Inventory audits and cycle count programs with protocols your team can run",
      "Shrinkage and variance root-cause analysis",
      "Safety stock and reorder point optimization",
      "Receiving, kitting and fulfillment SOPs — documented and repeatable",
      "Vendor coordination and on-time delivery follow-through",
    ],
  },
];

const PROCESS = [
  { icon: ClipboardList, step: "01", title: "Assess", body: "Walk the floor, map the current spreadsheet and BOM state, find where stock truth breaks down." },
  { icon: Database, step: "02", title: "Structure", body: "Clean the data — item master, BOMs, stock records — into a system-ready foundation." },
  { icon: Settings2, step: "03", title: "Implement", body: "Configure the MRP/ERP system, write the SOPs, stand up cycle counts and reorder logic." },
  { icon: GraduationCap, step: "04", title: "Hand off", body: "Train the team, document everything, and leave you with a system that runs without me." },
];

const AUDIENCE = [
  { icon: Building2, title: "Small-to-mid-size manufacturers", body: "Outgrowing spreadsheets, with assemblies that stall on parts nobody knew were short." },
  { icon: Building2, title: "Distributors on legacy accounting-only tools", body: "Inventory truth scattered across QuickBooks and guesswork, with no planning layer." },
  { icon: Users, title: "Teams needing interim planning leadership", body: "Contract, consulting or fractional coverage while you hire — or until the system runs itself." },
];

const REVEAL = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
};

export default function ServicesPage() {
  useEffect(() => {
    document.title =
      "MRP & Inventory Control Services in Poway & San Diego | Robert Daniels";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    if (desc) {
      desc.setAttribute(
        "content",
        "MRP & inventory control services for small-to-mid-size manufacturers and distributors in Poway and San Diego, CA — system migration, BOM optimization, operational auditing."
      );
    }
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "services-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "MRP & Inventory Control Services",
      serviceType:
        "Inventory Control, Material Requirements Planning (MRP), BOM Optimization, Operational Auditing",
      provider: {
        "@type": "Person",
        name: "Robert Daniels",
        url: "https://robertwdaniels.com/",
      },
      areaServed: ["Poway, CA", "San Diego, CA", "San Diego County, CA"],
      url: "https://robertwdaniels.com/services",
    });
    document.head.appendChild(script);
    return () => {
      document.title =
        "Robert Daniels — Inventory Control & MRP Specialist | Poway & San Diego";
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
      document.getElementById("services-jsonld")?.remove();
    };
  }, []);

  return (
    <div className="pt-[72px]">
      <section className="relative overflow-hidden py-20 sm:py-28">
        <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />
        <div
          className="absolute -top-24 right-1/4 w-[480px] h-[480px] rounded-full bg-[#2563EB]/15 blur-[140px]"
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...REVEAL} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00F0FF]">
              Services — Poway &amp; San Diego, CA
            </p>
            <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.08]">
              MRP &amp; inventory control services for growing{" "}
              <span className="text-[#00F0FF] text-glow">Poway &amp; San Diego</span> operations.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
              Structured inventory lifecycle management, accurate demand
              forecasting, and disciplined material planning — delivered as
              migration projects, consulting engagements, or interim roles for
              small-to-mid-size manufacturers and distributors.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.id}
              id={s.id}
              {...REVEAL}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              data-testid={`service-block-${s.id}`}
              className="sheen relative rounded-3xl border border-white/[0.07] bg-[#111827]/70 overflow-hidden"
            >
              <span
                className="ghost-numeral font-display font-extrabold text-7xl absolute top-4 right-6 select-none hidden sm:block"
                aria-hidden="true"
              >
                {s.num}
              </span>
              <div className="grid lg:grid-cols-2 gap-8 p-7 sm:p-10">
                <div>
                  <span className="h-11 w-11 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                    <s.icon size={19} />
                  </span>
                  <h2 className="mt-5 font-display font-bold text-2xl sm:text-3xl text-white tracking-tight">
                    {s.title}
                  </h2>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]/80">
                    {s.tag}
                  </p>
                  <p className="mt-4 text-[15px] text-[#9CA3AF] leading-relaxed">{s.intro}</p>
                </div>
                <div className="lg:border-l border-white/[0.08] lg:pl-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#6B7280]">
                    What's included
                  </p>
                  <ul className="mt-4 space-y-3">
                    {s.includes.map((item) => (
                      <li key={item} className="flex gap-3 text-sm text-[#D1D5DB] leading-relaxed">
                        <span className="mt-[7px] h-1 w-1 shrink-0 rotate-45 bg-[#00F0FF]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="relative py-16 sm:py-24 bg-[#0D1320]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...REVEAL} transition={{ duration: 0.7 }}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00F0FF]">
              How an engagement runs
            </p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white leading-[1.1]">
              Four steps, no mystery.
            </h2>
          </motion.div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.step}
                {...REVEAL}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="h-full rounded-2xl border border-white/[0.07] bg-[#111827]/70 p-6"
              >
                <div className="flex items-center justify-between">
                  <span className="h-10 w-10 rounded-lg border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                    <p.icon size={17} />
                  </span>
                  <span className="font-mono text-xs text-[#00F0FF]/70">{p.step}</span>
                </div>
                <h3 className="mt-5 font-display font-bold text-lg text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...REVEAL} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00F0FF]">
              Who it's for
            </p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white leading-[1.1]">
              Built for operations outgrowing their tools.
            </h2>
          </motion.div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {AUDIENCE.map((a, i) => (
              <motion.div
                key={a.title}
                {...REVEAL}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="h-full rounded-2xl border border-white/[0.07] bg-[#111827]/70 p-6"
              >
                <a.icon size={18} className="text-[#00F0FF]" />
                <h3 className="mt-4 font-display font-bold text-white leading-snug">{a.title}</h3>
                <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{a.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-16 sm:py-24 overflow-hidden">
        <img
          src={RACKS_IMG}
          alt="Warehouse racking — MRP and inventory control services in Poway and San Diego, CA"
          className="absolute inset-0 w-full h-full object-cover img-duotone opacity-[0.16]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F17] via-[#0B0F17]/85 to-[#0B0F17]/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...REVEAL} transition={{ duration: 0.7 }} className="max-w-2xl">
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
              Let's map your move from manual to automated.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
              Bring your current spreadsheet mess, static BOMs, or stockout
              headaches — the first conversation is about understanding your
              operation, not selling you a system.
            </p>
            <Link
              to="/"
              state={{ scrollTo: "#contact" }}
              data-testid="services-cta-contact"
              className="mt-9 inline-flex items-center gap-2.5 font-display font-semibold text-[#0B0F17] bg-[#00F0FF] hover:bg-[#4DF7FF] rounded-full px-8 py-4 shadow-[0_10px_40px_rgba(0,240,255,0.22)] transition-all duration-300 hover:-translate-y-0.5"
            >
              Schedule a Consultation / Hire Me
              <ArrowRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
